import { ALLOWED_LINKS_AS_TABS, AnalyticsEventTopic, getOafIdFromEvent } from '~/modules/analytics/analyticsHelper';
import { AnalyticsWeightings } from '../analyticsWeightings';
import { CoveoAnalyticsCustomData, CoveoAnalyticsEvent, CoveoEventType } from './coveoEventType';

interface CustomCoveoAnalyticsCustomData extends CoveoAnalyticsCustomData {
    source: string;
    contentIdValue: string;
    contentIdKey: string;
    listingName: string;
    listingCategoryName: string;
}

interface CustomCoveoAnalyticsEvent extends CoveoAnalyticsEvent {
    eventValue: string;
    eventType: string;
    customData: CustomCoveoAnalyticsCustomData;
}

export const COVEO_WHITE_PAPER_TYPE = 'whitePaper';
export const COVEO_TESTIMONIAL_TYPE = 'testimonial';
export const COVEO_WEBINAR_TYPE = 'webinar';
export const COVEO_CUSTOMIZATION_GUIDE_TYPE = 'customizationGuide';
export const COVEO_DATA_SHEET_TYPE = 'dataSheet';
export const COVEO_CASE_STUDY_TYPE = 'caseStudy';

abstract class CustomCoveoEventType extends CoveoEventType {
    // @ts-expect-error * Automated Type Error Fix
    protected abstract customSpecificBody;

    // @ts-expect-error * Automated Type Error Fix
    abstract canHandle(event): boolean;

    // @ts-expect-error * Automated Type Error Fix
    getBody(event): CustomCoveoAnalyticsEvent {
        const baseEvent = this.baseBody(event);
        const listingCategories = this.getListingCategories(event.listing?.listingCategories);
        const oafId = getOafIdFromEvent(event);
        return {
            ...baseEvent,
            eventValue: oafId,
            customData: {
                ...baseEvent.customData,
                source: 'Site',
                contentIdValue: oafId,
                contentIdKey: 'permanentId',
                listingName: event.listing?.title || event.title,
                listingCategoryName:
                    (listingCategories?.length && listingCategories[0]) || event.listingCategoryName || '',
            },
            ...this.customSpecificBody,
        };
    }

    getPath(): string {
        return 'custom';
    }
}

export class TileViewCoveoEventType extends CustomCoveoEventType {
    // @ts-expect-error * Automated Type Error Fix
    canHandle(event): boolean {
        return event.topic === AnalyticsEventTopic.ANALYTICS_LOAD_IMPRESSION && event.elementType === 'listingTile';
    }
    customSpecificBody = { eventType: 'tileView', outcome: AnalyticsWeightings.defaultPoints };
}

export class ScreenshotCoveoEventType extends CustomCoveoEventType {
    // @ts-expect-error * Automated Type Error Fix
    canHandle(event): boolean {
        return event.topic === AnalyticsEventTopic.ANALYTICS_CAROUSEL_SHOWN_IMPRESSION;
    }
    customSpecificBody = { eventType: 'screenshot', outcome: AnalyticsWeightings.screenShotPoints };
}

export class AppInstallCustomCoveoEventType extends CustomCoveoEventType {
    // @ts-expect-error * Automated Type Error Fix
    canHandle(event): boolean {
        return event.topic === '_AppInstall_';
    }
    customSpecificBody = { eventType: 'appInstall' };
}

export class WhitePaperCoveoEventType extends CustomCoveoEventType {
    // @ts-expect-error * Automated Type Error Fix
    canHandle(event): boolean {
        return event.topic === AnalyticsEventTopic.ANALYTICS_CLICK && event.customTitle?.toLowerCase() === 'whitepaper';
    }
    customSpecificBody = { eventType: COVEO_WHITE_PAPER_TYPE, outcome: AnalyticsWeightings.whitePaperPoints };
}

export class TestDriveCoveoEventType extends CustomCoveoEventType {
    // @ts-expect-error * Automated Type Error Fix
    canHandle(event): boolean {
        return event.topic === '_testDrive_';
    }
    customSpecificBody = { eventType: 'testDrive' };
}

export class DemoCoveoEventType extends CustomCoveoEventType {
    // @ts-expect-error * Automated Type Error Fix
    canHandle(event): boolean {
        return event.topic === '_demo_';
    }
    customSpecificBody = { eventType: 'demo', outcome: AnalyticsWeightings.demoPoints };
}

export class FavListingCoveoEventType extends CustomCoveoEventType {
    // @ts-expect-error * Automated Type Error Fix
    canHandle(event): boolean {
        return event.topic === '_favListing_';
    }
    customSpecificBody = { eventType: 'favListing' };
}

export class NegativeReviewCoveoEventType extends CustomCoveoEventType {
    // @ts-expect-error * Automated Type Error Fix
    canHandle(event): boolean {
        return event.topic === '_likeReview_';
    }
    customSpecificBody = { eventType: 'negativeReview' };
}

export class LearnMoreCoveoEventType extends CustomCoveoEventType {
    // @ts-expect-error * Automated Type Error Fix
    canHandle(event): boolean {
        return event.topic === '_likeReview_';
    }
    customSpecificBody = { eventType: 'learnMore' };
}

export class TestimonialCoveoEventType extends CustomCoveoEventType {
    // @ts-expect-error * Automated Type Error Fix
    canHandle(event): boolean {
        return (
            event.topic === AnalyticsEventTopic.ANALYTICS_CLICK &&
            event.customTitle?.toLowerCase() === 'customer testimonial'
        );
    }
    customSpecificBody = { eventType: COVEO_TESTIMONIAL_TYPE, outcome: AnalyticsWeightings.customerTestimonialPoints };
}

export class WebinarCoveoEventType extends CustomCoveoEventType {
    // @ts-expect-error * Automated Type Error Fix
    canHandle(event): boolean {
        return event.topic === AnalyticsEventTopic.ANALYTICS_CLICK && event.customTitle?.toLowerCase() === 'webinar';
    }
    customSpecificBody = { eventType: COVEO_WEBINAR_TYPE, outcome: AnalyticsWeightings.webinarPoints };
}

export class CustomizationGuideCoveoEventType extends CustomCoveoEventType {
    // @ts-expect-error * Automated Type Error Fix
    canHandle(event): boolean {
        return (
            event.topic === AnalyticsEventTopic.ANALYTICS_CLICK &&
            event.customTitle?.toLowerCase() === 'customization guide'
        );
    }
    customSpecificBody = {
        eventType: COVEO_CUSTOMIZATION_GUIDE_TYPE,
        outcome: AnalyticsWeightings.customizationGuidePoints,
    };
}

export class DataSheetCoveoEventType extends CustomCoveoEventType {
    // @ts-expect-error * Automated Type Error Fix
    canHandle(event): boolean {
        return event.topic === AnalyticsEventTopic.ANALYTICS_CLICK && event.customTitle?.toLowerCase() === 'data sheet';
    }
    customSpecificBody = { eventType: COVEO_DATA_SHEET_TYPE, outcome: AnalyticsWeightings.dataSheetPoints };
}

export class UninstallCoveoEventType extends CustomCoveoEventType {
    // @ts-expect-error * Automated Type Error Fix
    canHandle(event): boolean {
        return event.topic === '_likeReview_';
    }
    customSpecificBody = { eventType: 'uninstall' };
}

export class CaseStudyCoveoEventType extends CustomCoveoEventType {
    // @ts-expect-error * Automated Type Error Fix
    canHandle(event): boolean {
        return event.topic === AnalyticsEventTopic.ANALYTICS_CLICK && event.customTitle?.toLowerCase() === 'case study';
    }
    customSpecificBody = { eventType: COVEO_CASE_STUDY_TYPE, outcome: AnalyticsWeightings.caseStudyPoints };
}

export class TileClickCoveoEventType extends CustomCoveoEventType {
    // @ts-expect-error * Automated Type Error Fix
    canHandle(event): boolean {
        return event.topic === AnalyticsEventTopic.ANALYTICS_CLICK && event.elementType === 'listingTile';
    }
    customSpecificBody = { eventType: 'tileClick', outcome: AnalyticsWeightings.defaultPoints };
}

export class ListingTabSwitchCoveoEventType extends CustomCoveoEventType {
    // @ts-expect-error * Automated Type Error Fix
    canHandle(event): boolean {
        return (
            event.topic === AnalyticsEventTopic.ANALYTICS_CLICK &&
            (event.elementType === 'tab' ||
                (event.elementType === 'link' && ALLOWED_LINKS_AS_TABS.includes(event.link?.callToAction?.name)))
        );
    }
    customSpecificBody = { eventType: 'listingTabSwitch', outcome: AnalyticsWeightings.defaultPoints };
}
